import { useSnackbar } from "notistack";
import {
  getParticipantOnlinePresence,
  jobMessageList,
  updateMessageSeen,
} from "../endpoints/job.service";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchJobMessagesProfile,
  resetRoomUnread,
  setJobMembersProfile,
  setChatReceiverDetails,
} from "../store/actions/message.action";
import { useSocket } from "context/SocketProvider";
import { v4 as uuidv4 } from "uuid";
import { updateAndSortChatGroups } from "@utils/updateAndSortChatGroups";
import store from "@store/index";
import { RootState } from "@store/reducers";
import {
  setOnlineStatus,
  resetOnlineStatus,
} from "../store/actions/job.action";
import { mergeMessages } from "@utils/mergeMessages";
import useChatTyping from "./useChatTyping";
import useChatMembership from "./useChatMembership";
import { applyReadReceipt, isUnreadIncomingMessage } from "../utils/chatReadReceipts";
import { EnumUserTypes } from "../utils/constants";

const LIMIT = 100;

export const MESSAGE_EVENTS = {
  JOIN_ROOM: "chat.join",
  LEAVE_ROOM: "chat.leave",
  SEND_MESSAGE: "job:chat:send",
  RECEIVE_MESSAGE: "job:chat:receive",
  TYPING: "job:chat:typing",
  REFRESH_MESSAGES: "job:chat:refresh",
  PRESENCE: "presence.update",
};

const useChat = ({ jobId, receiverDetails, fetchInbox = false }) => {
  const dispatch = useDispatch();
  const socketRef = useSocket();
  const socket = socketRef?.current;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state: RootState) => state.auth);

  // Local state
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [isMessageLoading, setIsMessageLoading] = useState(true);
  const [isOlderMessageLoading, setIsOlderMessageLoading] = useState(false);
  const onlineStatus = useSelector(
    (state: RootState) => state.job.onlineStatus,
  );

  const recipientUserId = receiverDetails?.userId;
  const conversationKey = `${user?._id}:${jobId}:${recipientUserId}`;
  const activeConversation = useRef(conversationKey);
  const refreshRequest = useRef(0);
  activeConversation.current = conversationKey;
  const [permission, setPermission] = useState<{ key: string; canSend: boolean; message?: string } | null>(null);
  const candidateJobChat = user?.userType === EnumUserTypes.CANDIDATE && !jobId?.startsWith("network:") && !jobId?.startsWith("requirement:");
  const canSendMessage = permission?.key === conversationKey ? permission.canSend : !candidateJobChat;
  const messagingRestrictionReason = canSendMessage ? null : permission?.key === conversationKey
    ? permission.message || "Messaging is currently unavailable."
    : "Checking messaging permissions...";
  const isTyping = useChatTyping(socket, jobId, recipientUserId);
  useChatMembership(socket, jobId, recipientUserId);

  const appendLocalMessage = useCallback((message) => {
    if (!message) return;
    setMessages((prev) => mergeMessages(prev, [message]).reverse());
  }, []);

  const loadOlderMessages = useCallback(async () => {
    setIsOlderMessageLoading(true);
    jobMessageList({
      jobId,
      receiverId: recipientUserId,
      cursor: nextCursor,
    })
      .then((response) => {
        if (activeConversation.current !== conversationKey) return;
        setHasNextPage(response?.data?.pagination?.hasNextPage);
        setNextCursor(response?.data?.pagination?.nextCursor);
        // setPage((prev) => prev + 1);
        const olderMessages = response?.data?.data?.reverse();
        console.log(
          "check older messages",
          olderMessages,
          response?.data?.messages,
        );
        setMessages((prev) => mergeMessages(prev, olderMessages).reverse());
      })
      .catch((error) => {
        console.error("❌ useChat: Failed to fetch older message list", error);
      })
      .finally(() => {
        if (activeConversation.current === conversationKey) setIsOlderMessageLoading(false);
      });
  }, [jobId, recipientUserId, nextCursor, conversationKey]);

  const refreshMessages = useCallback(async () => {
    if (!jobId || !recipientUserId) return;
    const requestId = ++refreshRequest.current;

    try {
      const response = await jobMessageList({
        receiverId: recipientUserId,
        jobId,
        limit: LIMIT,
      });
      if (activeConversation.current !== conversationKey || refreshRequest.current !== requestId) return;
      setPermission({ key: conversationKey, canSend: response?.data?.messagingPermission?.canSend ?? !candidateJobChat,
        message: response?.data?.messagingPermission?.message });
      setHasNextPage(response?.data?.pagination?.hasNextPage);
      setNextCursor(response?.data?.pagination?.nextCursor);
      const formattedData = response?.data?.data;
      setMessages((prev) => mergeMessages(prev, formattedData || []).reverse());
      dispatch(fetchJobMessagesProfile());
    } catch (error) {
      if (candidateJobChat && activeConversation.current === conversationKey && refreshRequest.current === requestId) {
        setPermission({ key: conversationKey, canSend: false, message: "Unable to check messaging permissions. Please try again." });
      }
      console.error("❌ useChat: Failed to refresh message list", error);
    } finally {
      if (activeConversation.current === conversationKey && refreshRequest.current === requestId) setIsMessageLoading(false);
    }
  }, [jobId, recipientUserId, dispatch, conversationKey, candidateJobChat]);

  useEffect(() => {
    const onFocus = () => refreshMessages();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshMessages]);

  // Send message handler
  const sendMessage = useCallback(
    async ({
      message,
      setMessage,
      attachments = [],
      setAttachments = null,
      replyTo = null,
    }) => {
      if (!recipientUserId) return;
      if (!canSendMessage) {
        enqueueSnackbar(messagingRestrictionReason, { variant: "warning" });
        return;
      }
      const hasContent = !!message?.trim();
      const hasAttachments =
        Array.isArray(attachments) && attachments.length > 0;
      if (!hasContent && !hasAttachments) return;
      try {
        setIsMessageSending(true);

        try {
          const clientMsgId = uuidv4();
          const payload = {
            jobId,
            recipientId: recipientUserId,
            content: message || "",
            clientMsgId,
            attachments,
            ...(replyTo?.messageId
              ? { replyToMessageId: replyTo.messageId }
              : {}),
          };
          console.log("send message payload", payload);
          const updatedChatGroups = updateAndSortChatGroups(
            store.getState()?.messageReducer?.membersProfile,
            {
              ...payload,
              senderId: user?._id,
              createdAt: new Date().toString(),
            },
          );
          dispatch(setJobMembersProfile(updatedChatGroups));
          socket.emit(MESSAGE_EVENTS.SEND_MESSAGE, payload);
          // Add temporary message to UI immediately. Idempotent: the server echo
          // carrying the same clientMsgId collapses onto this placeholder instead
          // of appending a duplicate.
          const optimisticMessage = {
            clientMsgId,
            message: message || "",
            attachments,
            createdName: user?.firstName + " " + user?.lastName,
            seen: false,
            createdBy: user?._id || "",
            createdAt: new Date(),
            isTemp: true,
            ...(replyTo ? { replyTo } : {}),
          };
          setMessages((prev) =>
            mergeMessages(prev, [optimisticMessage]).reverse(),
          );
          // console.log("Socket send message payload", receiverDetails, payload);
        } catch (error) {
          console.log(error, "logging here error in temp message emit");
        }

        setMessage("");
        if (typeof setAttachments === "function") {
          setAttachments([]);
        }
      } catch (error) {
        enqueueSnackbar("Error sending message: " + error.message, {
          variant: "error",
        });
      } finally {
        setIsMessageSending(false);
      }
    },
    [recipientUserId, jobId, user, dispatch, socket, enqueueSnackbar, canSendMessage, messagingRestrictionReason],
  );

  const triggerTyping = useCallback(
    (status) => {
      if (status && !canSendMessage) return;
      const payload = {
        jobId,
        recipientId: recipientUserId,
        typing: status,
      };
      socket?.emit(MESSAGE_EVENTS.TYPING, payload);
    },
    [jobId, recipientUserId, socket, canSendMessage],
  );

  // ----------------
  // HANDLE PRESENCE EVENT
  // ----------------
  const handlePresenceEvent = (data: any) => {
    if (data?.userId === recipientUserId) {
      dispatch(setOnlineStatus(data));
    }
  };

  // useEffect(() => {
  //   if (!socket) return;

  //   const handleTempMessage = (msg) => {
  //     const isSameJobId = msg?.jobApplicationId === jobId;
  //     const isCurrentUsersMessage = msg?.createdBy === user?._id;

  //     if (isSameJobId && isCurrentUsersMessage) {
  //       setMessages((prev) => {
  //         return [
  //           ...prev,
  //           {
  //             chatId: msg.chatId,
  //             message: msg.message,
  //             createdName: msg.createdName,
  //             seen: msg.seen,
  //             createdBy: msg.createdBy,
  //             isTemp: msg.isTemp,
  //           },
  //         ];
  //       });
  //     }
  //   };

  //   socket.on("tempMessage", handleTempMessage);

  //   return () => {
  //     socket.off("tempMessage", handleTempMessage);
  //   };
  // }, [socket, jobId]);

  useEffect(() => {
    if (!recipientUserId) return;
    const fetchParticipantOnlinePresence = async () => {
      try {
        const { data: participantOnlinePresence } =
          await getParticipantOnlinePresence(recipientUserId);
        if (
          participantOnlinePresence &&
          participantOnlinePresence.userId === recipientUserId
        ) {
          dispatch(setOnlineStatus(participantOnlinePresence));
        }
      } catch (error) {
        console.error("Error fetching particiapnt online presence", error);
      }
    };
    fetchParticipantOnlinePresence();

    return () => {
      dispatch(resetOnlineStatus());
    };
  }, [recipientUserId, dispatch]);

  // Socket Listener with comprehensive debugging
  useEffect(() => {
    if (!socket) {
      return;
    }

    const handleNewMessage = (messageData) => {
      console.log(
        "useChat: New message",
        messageData,
        jobId,
        receiverDetails?.userId,
      );

      // Accept both the other participant's messages AND our own echo (the
      // server broadcasts it back with the same clientMsgId, which collapses onto
      // the optimistic placeholder and upgrades it with the real messageId).
      const selfId = user?._id;
      const isFromReceiver = messageData?.senderId === recipientUserId;
      const isOwnEcho =
        messageData?.senderId === selfId &&
        messageData?.recipientId === recipientUserId;
      const isRelevant =
        messageData?.jobId === jobId && (isFromReceiver || isOwnEcho);

      if (isRelevant) {
        if (isFromReceiver && !isOwnEcho && candidateJobChat && !canSendMessage) refreshMessages();
        // console.log("✅ useChat: Message matches current chat - ADDING TO UI");
        const newMessage = {
          ...messageData,
          attachments: messageData?.attachments || [],
        };
        setMessages((prev) => mergeMessages(prev, [newMessage]).reverse());
      }
    };

    const handleRefreshMessages = (messageData) => {
      return;
      const isSameJobId = messageData?.jobId === jobId;
      const isFromReceiver = messageData?.senderId === recipientUserId;

      if (isSameJobId && isFromReceiver) {
        refreshMessages();
      }
    };

    const handleMessageRead = (messageResponse) => {
      setMessages((prev) => applyReadReceipt(prev, messageResponse));
    };

    const handleChatError = (error) => {
      if (error?.code !== "CANDIDATE_MESSAGING_RESTRICTED" || error.jobId !== jobId || error.recipientId !== recipientUserId) return;
      if (error.clientMsgId) setMessages(current => current.filter(message => message.clientMsgId !== error.clientMsgId));
      setPermission({ key: conversationKey, canSend: false, message: error.message });
      dispatch(fetchJobMessagesProfile());
      enqueueSnackbar(error.message, { variant: "warning" });
    };

    // Debug socket connection state
    const handleConnect = () => {
      // Recover messages delivered while the transport/server was unavailable.
      refreshMessages();
    };

    const handleDisconnect = (reason) => {
      // console.log("🔌 useChat: Socket disconnected", reason);
    };

    const handleConnectError = (error) => {
      // console.error("❌ useChat: Socket connection error", error.message);
    };

    // Remove existing listeners to avoid duplicates
    socket.off(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
    socket.off(MESSAGE_EVENTS.PRESENCE, handlePresenceEvent);
    // socket.off(MESSAGE_EVENTS.REFRESH_MESSAGES, handleRefreshMessages);
    // socket.off("tempMessage", handleNewMessage);
    socket.off("messageReadByReceiver", handleMessageRead);
    socket.off("connect", handleConnect);
    socket.off("disconnect", handleDisconnect);
    socket.off("connect_error", handleConnectError);

    // Add new listeners
    // socket.on("newMessage", handleNewMessage);
    socket.on(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
    socket.on(MESSAGE_EVENTS.PRESENCE, handlePresenceEvent);
    // socket.on(MESSAGE_EVENTS.REFRESH_MESSAGES, handleRefreshMessages);
    // if(jobId){
    //   socket
    // }
    // socket.on("tempMessage", handleNewMessage);
    socket.on("messageReadByReceiver", handleMessageRead);
    socket.on("readMessage", handleMessageRead);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on("chat:error", handleChatError);

    // console.log("✅ useChat: Socket listeners setup complete");

    // Cleanup on component unmount or dependency change
    return () => {
      // console.log("🧹 useChat: Cleaning up socket listeners");
      socket.off(MESSAGE_EVENTS.RECEIVE_MESSAGE, handleNewMessage);
      socket.off(MESSAGE_EVENTS.PRESENCE, handlePresenceEvent);
      // socket.off(MESSAGE_EVENTS.REFRESH_MESSAGES, handleRefreshMessages);
      socket.off("messageReadByReceiver", handleMessageRead);
      socket.off("readMessage", handleMessageRead);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("chat:error", handleChatError);
    };
  }, [socket, jobId, recipientUserId, user?._id, dispatch, refreshMessages, conversationKey, enqueueSnackbar, candidateJobChat, canSendMessage]);

  // Fetching list of messages.
  // Reset per-conversation state first so mergeMessages can't blend the previous
  // chat's rows (and stale pagination) into the newly opened one — `messages`,
  // `nextCursor`, and `hasNextPage` all persist across jobId/recipient changes.
  useEffect(() => {
    console.log("Fetching params", jobId, receiverDetails);
    setMessages([]);
    setNextCursor(null);
    setHasNextPage(false);
    setIsOlderMessageLoading(false);
    if (jobId && recipientUserId) {
      setIsMessageLoading(true);
      refreshMessages();
    } else {
      setIsMessageLoading(false);
    }
  }, [recipientUserId, jobId]);

  // Optimistically clear this room's unread badge the moment it is opened,
  // independent of the mark-seen API (server is persisted by the effect below).
  useEffect(() => {
    if (!fetchInbox && jobId && user?._id && recipientUserId) {
      dispatch(setChatReceiverDetails({ jobId, participantId: recipientUserId }));
      dispatch(resetRoomUnread({ jobId, userId: user?._id, participantId: recipientUserId }));
      return () => {
        const active = store.getState().messageReducer?.chatReceiverDetails;
        if (active?.jobId === jobId && active?.participantId === recipientUserId) dispatch(setChatReceiverDetails(null));
      };
    }
  }, [jobId, recipientUserId, user?._id, fetchInbox, dispatch]);

  // Mark messages as seen
  useEffect(() => {
    if (fetchInbox) {
      // console.log("📨 useChat: Fetching inbox messages");
      dispatch(fetchJobMessagesProfile());
      return;
    }

    if (messages?.length > 0) {
      const selfIds = [user?._id, user?.userId].filter(Boolean);
      const unseenMessages = messages?.filter(
        (item) => isUnreadIncomingMessage(item, recipientUserId, selfIds),
      );
      console.log("unseen messages", unseenMessages);
      if (unseenMessages.length > 0) {
        // console.log("👀 useChat: Marking messages as seen", {
        //   unseenMessages,
        //   count: unseenMessages.length,
        //   messageIds: unseenMessages.map((m) => m.messageId),
        // });

        const unseenMessageIds = unseenMessages
          .map((message) => message?.messageId || message?._id)
          .filter(Boolean); // Filter out temporary message IDs

        if (unseenMessageIds.length > 0) {
          updateMessageSeen({ messageIds: unseenMessageIds })
            .then(() => {
              // console.log("✅ useChat: Messages marked as seen");
              // Optimistically clear this room's badge; focus reconcile corrects drift.
              setMessages((current) => current.map((message) => unseenMessageIds.includes(message.messageId || message._id)
                ? { ...message, seen: true } : message));
              dispatch(resetRoomUnread({ jobId, userId: user?._id, participantId: recipientUserId }));
            })
            .catch((error) => {
              // console.error(
              //   "❌ useChat: Failed to mark messages as seen",
              //   error,
              // );
            });
        }
      }
    }
  }, [messages, fetchInbox, user?._id, user?.userId, jobId, recipientUserId, dispatch]);

  // console.log("💬 useChat: Current state", {
  //   messageCount: messages.length,
  //   isLoading: isMessageLoading,
  //   isSending: isMessageSending,
  //   hasSocket: !!socket,
  //   socketConnected: socket?.connected,
  // });

  return {
    canSendMessage,
    messagingRestrictionReason,
    refreshMessagingPermission: refreshMessages,
    isMessageLoading,
    messages,
    isMessageSending,
    sendMessage,
    appendLocalMessage,
    loadOlderMessages,
    isOlderMessageLoading,
    triggerTyping,
    isTyping,
    hasNextPage,

    onlineStatus,
  };
};

export default useChat;
